import {
    SearchFacetInput,
    SearchFacetValueInput,
    SearchIndexField,
} from '__generated__/graphql'

type FilterCodec = {
    facet: SearchIndexField
    encode: (value: SearchFacetValueInput) => string | null
    decode: (str: string) => SearchFacetValueInput
}

const stringArray = (facet: SearchIndexField): FilterCodec => ({
    facet,
    encode: (v) => (v.stringArray?.length ? v.stringArray.join('_') : null),
    decode: (s) => ({ stringArray: s.split('_').filter(Boolean) }),
})

const rosterCreatedAt: FilterCodec = {
    facet: SearchIndexField.RosterCreatedAt,
    encode: (v) => {
        if (!v.dateRange?.gte && !v.dateRange?.lte) return null
        const format = (iso: string | null | undefined): string => {
            if (!iso) return ''
            const d = new Date(iso)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            return `${year}-${month}`
        }
        return `${format(v.dateRange.gte)}_${format(v.dateRange.lte)}`
    },
    decode: (s) => {
        const [gteStr, lteStr] = s.split('_')
        const parseGte = (str: string): string | null => {
            if (!str) return null
            const [year, month] = str.split('-').map(Number)
            if (!year || !month) return null
            return new Date(year, month - 1, 1).toISOString()
        }
        const parseLte = (str: string): string | null => {
            if (!str) return null
            const [year, month] = str.split('-').map(Number)
            if (!year || !month) return null
            return new Date(year, month, 0, 23, 59, 59, 999).toISOString()
        }
        return { dateRange: { gte: parseGte(gteStr), lte: parseLte(lteStr) } }
    },
}

const string = (facet: SearchIndexField): FilterCodec => ({
    facet,
    encode: (v) => v.string || null,
    decode: (s) => ({ string: s }),
})

const FILTER_REGISTRY: Record<string, FilterCodec> = {
    company: stringArray(SearchIndexField.RosterCompany),
    createdBy: stringArray(SearchIndexField.RosterCreatedBy),
    createdAt: rosterCreatedAt,
    status: stringArray(SearchIndexField.RosterStatus),
    type: string(SearchIndexField.RosterType),
    name: string(SearchIndexField.RosterName),
    quickSearch: string(SearchIndexField.RosterQuickSearch),
    internalUsers: stringArray(SearchIndexField.RosterInternalUsers),
    industries: stringArray(SearchIndexField.RosterIndustries),
    geoLocations: stringArray(SearchIndexField.RosterGeoLocations),
    geoLocationText: stringArray(SearchIndexField.RosterGeoLocationText),
    sectors: stringArray(SearchIndexField.RosterSectors),
    startDate: stringArray(SearchIndexField.RosterStartDate),
    processStatus: stringArray(SearchIndexField.RosterCandidateProcessStatus),
    titles: stringArray(SearchIndexField.RosterTitles),
}

const FACET_TO_KEY = Object.fromEntries(
    Object.entries(FILTER_REGISTRY).map(([key, codec]) => [codec.facet, key])
) as Record<SearchIndexField, string>

export function encodeFilters(filters: SearchFacetInput[]): string {
    const parts: string[] = []
    for (const filter of filters) {
        const key = FACET_TO_KEY[filter.facet]
        if (!key) continue
        const codec = FILTER_REGISTRY[key]
        const encoded = codec.encode(filter.value)
        if (encoded) {
            parts.push(`${key}~${encoded}`)
        }
    }
    return parts.join('.')
}

export function decodeFilters(str: string): SearchFacetInput[] {
    if (!str) return []
    const filters: SearchFacetInput[] = []
    const parts = str.split('.').filter(Boolean)
    for (const part of parts) {
        const tildeIdx = part.indexOf('~')
        if (tildeIdx === -1) continue
        const key = part.slice(0, tildeIdx)
        const value = part.slice(tildeIdx + 1)
        const codec = FILTER_REGISTRY[key]
        if (!codec) continue
        filters.push({
            facet: codec.facet,
            value: codec.decode(value),
        })
    }
    return filters
}
